import { Document } from '@/interfaces/document';
import { DocumentValue } from '@/interfaces/documentValue';
import { DocumentResource, documentResource } from '@/resources/document.resource';
import attachment from '@/store/modules/document/attachment.module';
import status from '@/store/modules/document/status.module';
import { EntityModule } from '@/store/modules/entity.module';

/**
 * Moduł Dokumentu
 *
 * @export
 * @class DocumentModule
 * @extends {EntityModule<Document, DocumentResource>}
 */
export class DocumentModule extends EntityModule<Document, DocumentResource> {
  public modules = { status, attachment };
  constructor() {
    super(documentResource);

    (this.state as any) = {
      ...this.state,
      documentValues: [],
      hasNoDuplicates: undefined
    };

    this.actions = {
      ...this.actions,

      /**
       * Pobiera dokument
       *
       * @param {*} context
       * @param {(number|{id: number, preventLoader: boolean})} data
       * @returns {Promise<Document>}
       */
      get: (context: any, data?: number | { id: number; preventLoader: boolean }): Promise<Document> => {
        const id = data && typeof data === 'object' ? data.id : data;
        const preventLoader = data && typeof data === 'object' ? data.preventLoader : false;
        return this.resource.get(id || context.state.id, preventLoader).then(entity => {
          context.commit('setEntity', entity);
          return entity;
        });
      },

      /**
       * Aktualizuje dokument
       *
       * @param {*} context
       * @param {*} data
       * @returns {Promise<number>}
       */
      update: (context: any, data: any): Promise<number> => {
        return this.resource
          .update(data.id as number, { ...data, preventLoader: undefined }, data.preventLoader)
          .then(id => {
            context.commit('update', data);
            context.commit('setEntity', data);
            return id;
          });
      },

      /**
       * Ustawia status dokumentu
       *
       * @param context context
       * @param data dane do wysłania do API
       */
      setStatus: (context: any, data: any): Promise<number> =>
        this.resource.setStatus(data.id, data.statusId, data.comment),

      /**
       * Ustawia statusy dokumentów
       *
       * @param context context
       * @param data dane do wysłania do API
       */
      setStatuses: (context: any, data: any): Promise<number> =>
        this.resource.setStatuses(data.ids, data.statusId, data.comment),

      /**
       * Aktualizuje numery systemowe dokumentu
       *
       * @param {*} _
       * @param {*} data
       * @returns {Promise<number>}
       */
      updateSystemNumbers: (_, data: any): Promise<number> =>
        this.resource.updateSystemNumbers(data.id, { ...data, id: undefined }),

      /**
       * Dodaje encję
       *
       * @param context context
       * @param data dane do wysłania do API
       */
      add: (context: any, data: any): Promise<number> => this.resource.add(data),

      /**
       * Waliduje dane wrażliwe, niemożliwe do zwalidowania przez schemę
       *
       * @param {*} _
       * @param {*} data
       * @returns {Promise<boolean>}
       */
      validateSensitiveData: (_, data: any): Promise<boolean> =>
        this.resource.validateData(data, 'documentSensitiveDataValidation'),

      /**
       * Waliduje powtarzające się dane
       *
       * @param {*} _
       * @param {*} data
       * @returns {Promise<boolean>}
       */
      validateDuplicatedData: (context: any, data: any): Promise<boolean> =>
        this.resource.validateData(data, 'documentDuplicatedDataValidation').then(hasNoDuplicates => {
          context.commit('saveNoDuplicates', hasNoDuplicates);
          return hasNoDuplicates;
        }),

      /**
       * Pobiera wartości DocumentValues dokumentu
       *
       * @param {*} context
       * @param {*} id
       * @returns {Promise<any>}
       */
      getDocumentValues: (context: any, id: any): Promise<any> => {
        return this.resource
          .getDocumentValues(id)
          .then(document => context.commit('setDocumentValues', document.documentValues));
      }
    };

    this.mutations = {
      ...this.mutations,

      /**
       * Ustawienie w state documentVales
       *
       * @param {*} state
       * @param {DocumentValue[]} documentValues
       */
      setDocumentValues(state: any, documentValues: DocumentValue[]): void {
        state.documentValues = documentValues;
      },

      /**
       * Ustawia w state informacje o duplikatach
       *
       * @param {*} state
       * @param {boolean} hasNoDuplicates
       */
      saveNoDuplicates(state: any, hasNoDuplicates: boolean): void {
        state.hasNoDuplicates = hasNoDuplicates;
      }
    };
  }
}

export default new DocumentModule();
